var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { graphHeight } from "ui/components/panels/graphs/constants";
import { graphPanelWidth } from "ui/components/panels/graphs/graph.style";
import { simpleBoxHeight } from "ui/components/panels/dashboard/boxes/simple-box.style";
/**
 * Vertical room reserved at the top of the canvas overlay for the topbar. The
 * open graph panel grows upward from the dashboard and must not slide under the
 * topbar (which would hide its close button).
 *
 * On short screens every vertical pixel matters: a smaller reserve lets the
 * panel scale down less, which in turn shrinks the leftover horizontal space to
 * the right of the dashboard. On tall screens the panel fits comfortably, so we
 * keep a generous reserve. The reserve is ramped (not stepped) between the two
 * so the panel's scale stays monotonic as the window is resized — a hard
 * threshold would make the panel shrink when the window grows taller.
 *
 * (On tall screens width is the binding constraint anyway, so the reserve only
 * affects the scale on short screens — exactly where we want the smaller one.)
 */
/**
 * The dashboard's natural height with the graph panel open: the panel grows
 * upward from the dashboard's bottom edge by its own height plus the bottom
 * offset it sits at (see graph.style.tsx).
 */
export var dashboardOpenHeight = graphHeight + simpleBoxHeight + 5;
var MIN_TOP_INSET = 40;
var MAX_TOP_INSET = 90;
var SHORT_SCREEN_HEIGHT = 900; // at/below: MIN_TOP_INSET
var TALL_SCREEN_HEIGHT = 1080; // at/above: MAX_TOP_INSET
var topInsetForHeight = function (viewportHeight) {
    var ramp = (viewportHeight - SHORT_SCREEN_HEIGHT) /
        (TALL_SCREEN_HEIGHT - SHORT_SCREEN_HEIGHT);
    var clamped = Math.min(1, Math.max(0, ramp));
    return MIN_TOP_INSET + (MAX_TOP_INSET - MIN_TOP_INSET) * clamped;
};
/**
 * The entire dashboard — box row plus the absolutely-positioned graph panel —
 * is built from hardcoded, mutually-dependent pixel sizes. All alignment
 * (PlayLine across panels, graph traces sitting above the chart SVG) only holds
 * because those pixels are internally consistent.
 *
 * Rather than make every box responsive (which would break that alignment), we
 * scale the whole coupled unit uniformly. A uniform affine scale preserves
 * every internal relationship for free, so nothing realigns; the only effect is
 * that a narrow or short viewport shrinks the dashboard until it fits — which,
 * critically, brings the graph panel's close button back on-screen.
 *
 * The returned scale is applied as `transform: scale()` on the dashboard
 * `Content` container (origin bottom-left, matching the dashboard's anchoring).
 *
 * - Width is always constrained so the box row never overflows the viewport.
 * - Height is only constrained while the graph panel is open, since only then
 *   does the dashboard need vertical room beyond the box row itself.
 *
 * `containerRef` should point at the element whose layout width is the
 * available budget for the box row (the `Content` container). CSS transforms do
 * not affect `clientWidth`, so measuring the scaled element itself is safe and
 * cannot feed back into a resize loop.
 */
export var useDashboardScale = function (containerRef, isGraphPanelOpen) {
    var theme = useTheme();
    var _a = __read(useState(1), 2), scale = _a[0], setScale = _a[1];
    useEffect(function () {
        var element = containerRef.current;
        if (!element)
            return;
        var naturalWidth = graphPanelWidth(theme);
        var recompute = function () {
            var widthScale = element.clientWidth / naturalWidth;
            var heightScale = isGraphPanelOpen
                ? (window.innerHeight - topInsetForHeight(window.innerHeight)) /
                    dashboardOpenHeight
                : Infinity;
            setScale(Math.min(1, widthScale, heightScale));
        };
        recompute();
        var observer = new ResizeObserver(recompute);
        observer.observe(element);
        window.addEventListener("resize", recompute);
        return function () {
            observer.disconnect();
            window.removeEventListener("resize", recompute);
        };
    }, [containerRef, theme, isGraphPanelOpen]);
    return scale;
};
